export const VIEWS_TITLE = 'Views'

export const documentTitleForView = (viewName?: string) => (viewName ? `${viewName} · Issues` : 'Issues')

export const VIEWS_LABELS = {
  actionsLabel: 'Actions',
  backToViews: 'Back to views',
  color: 'Color',
  copyNamePrefix: (name: string) => `[COPY] ${name}`,
  createDescription: 'Anyone with repository access can see this view.',
  createError: 'Could not create a new view',
  createLink: 'Create view',
  createdBy: (login: string) => `Created by ${login}`,
  defaultName: 'Untitled view',
  delete: 'Delete',
  deleteConfirmationButton: 'Delete',
  deleteContent: (viewName: string) => `Are you sure you want to delete view ${viewName}?`,
  deleteError: 'Could not delete view',
  deleteSuccess: (viewName: string) => `View ${viewName} deleted`,
  deleteSuccessLastSectionView: (viewName: string, sectionTitle: string) =>
    `View ${viewName} deleted. No ${sectionTitle} views remaining.`,
  deleteSuccessLastView: (viewName: string) => `View ${viewName} deleted. No saved views remaining.`,
  deleteTitle: 'Delete view',
  displayErrorDescription: 'Something went wrong displaying this view. Try reloading the page.',
  displayErrorTitle: 'Couldn’t display this view',
  duplicate: 'Duplicate',
  duplicateBlankNameError: 'Name cannot be blank',
  duplicateConfirmationButton: 'Duplicate view',
  duplicateContent: (viewName: string) => `Create a copy of “${viewName}” as a new saved view.`,
  duplicateError: 'Could not duplicate view',
  duplicateNameLabel: 'New view name',
  duplicateTitle: 'Duplicate view',
  edit: 'Edit view',
  editButtonAriaLabel: 'Saved views menu',
  editViewAriaLabel: (shortcutName: string) => `Edit ${shortcutName}`,
  editViewDescriptionAriaLabel: (shortcutName: string) => `Edit ${shortcutName} description`,
  editViewTitleAriaLabel: (shortcutName: string) => `Edit ${shortcutName} title`,
  emptyDescription: 'Save a search as a view. Views are visible to everyone with repository access.',
  emptyDescriptionTeamPersonal: 'Create personal or team views to track work.',
  emptyHeading: 'No views yet',
  emptyPersonal: 'No personal views yet',
  emptyPersonalCta: 'Create a personal view',
  emptyPersonalCtaSuffix: 'to track your work.',
  emptyTeam: 'No team views yet',
  emptyTeamCta: 'Create a team view',
  emptyTeamCtaSuffix: 'for everyone in this repository.',
  filterQueryLabel: 'Query',
  filterQueryPlaceholder: 'Search issues',
  icon: 'Icon',
  iconAndColorAnchorAriaLabel: 'Change icon and color',
  indexDisplayErrorDescription: 'We couldn’t display your views. Try reloading the page.',
  indexErrorTitle: 'Something went wrong',
  indexLoadErrorDescription: 'We couldn’t load your views. Please try again.',
  itemCount: (count: number) => `${count} ${count === 1 ? 'item' : 'items'}`,
  lastUpdated: 'Last updated',
  loadErrorDescription: 'Something went wrong loading this view. Please try again.',
  loadErrorTitle: 'Couldn’t load this view',
  loadingSavedViews: 'Loading saved views',
  loadingSelectedTeams: 'Loading selected teams',
  manageTeamsDescription: 'Choose teams that show up in your sidebar.',
  maxViewsDescription: 'Please remove an existing view before creating a new one.',
  maxViewsReached: 'You have reached the maximum number of saved views',
  maxViewsTooltip: 'Maximum number of views reached',
  noTeamsSelectedDescription: 'Choose teams that show up in your sidebar.',
  noTeamsSelectedHeading: 'No teams selected.',
  notFoundDescription: 'It may have been deleted, or the URL may be incorrect.',
  notFoundTitle: 'View not found',
  pin: 'Pin view',
  pinError: (viewName: string) => `Could not pin ${viewName}`,
  pinLimitReached: (max: number) => `Maximum of ${max} pins reached. Unpin an item to pin another.`,
  pinned: 'Pinned',
  pinSuccess: (viewName: string) => `View ${viewName} pinned`,
  personal: 'Personal',
  unpin: 'Unpin view',
  unpinError: (viewName: string) => `Could not unpin ${viewName}`,
  unpinSuccess: (viewName: string) => `View ${viewName} unpinned`,
  retrying: 'Retrying',
  saveChangesToNewView: 'Save changes to new view',
  saveChangesToView: 'Save changes',
  saving: 'Saving…',
  showMore: 'Show more',
  selectedTeams: 'Currently selected',
  teams: 'Teams',
  team: 'Team',
  unsavedChangesCancel: 'Keep editing',
  unsavedChangesConfirm: 'Discard',
  unsavedChangesContent: 'Are you sure you want to discard them?',
  unsavedChangesTitle: 'You have unsaved changes',
  updateError: 'Could not update the view',
  viewOptionsAriaLabel: (viewName: string) => `${viewName} options`,
  viewsListTitle: 'Saved views',
} as const

export const LABELS = {
  documentTitleForView,
  issueSearchInputAriaLabel: 'Search Issues',
  issueSearchInputPlaceholder: 'Search Issues',
  viewsTitle: VIEWS_TITLE,
  sortBy: 'Sort by',
  views: VIEWS_LABELS,
} as const

export const BUTTON_LABELS = {
  cancel: 'Cancel',
  newIssue: 'New issue',
  newView: 'New view',
  save: 'Save',
  tryAgain: 'Try again',
  reloadPage: 'Reload page',
} as const

export const VALUES = {
  // Mirrors RepositorySearchShortcut::MAX_PINNED_PER_REPOSITORY in dotcom.
  maxPinnedViews: 10,
} as const
